import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TrendingUp, DollarSign, Users, Calendar, BarChart3 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const PERIODS = [
  { id: 'daily', label: 'Today' },
  { id: 'weekly', label: 'This week' },
  { id: 'monthly', label: 'This month' },
  { id: 'all', label: 'All time' },
];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 uppercase">{label}</span>
      <Icon className="h-4 w-4 text-pink-500" />
    </div>
    <div className="text-2xl font-bold">{value}</div>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </Card>
);

const OperatorRevenue = () => {
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get(`/operator/me/revenue?period=${period}`);
      setData(data);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => `K${(n || 0).toFixed(2)}`;
  const maxSeries = data?.dailySeries?.length ? Math.max(...data.dailySeries.map(d => d.revenue), 1) : 1;

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10">
      <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="operator-revenue-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Revenue Dashboard</h1>
            <p className="text-sm text-gray-500">Daily settlement, commission breakdown, and trip-by-trip manifests.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => (
              <Button
                key={p.id} size="sm" variant={period === p.id ? 'default' : 'outline'}
                onClick={() => setPeriod(p.id)}
                className={period === p.id ? 'bg-pink-600 hover:bg-pink-700' : ''}
                data-testid={`operator-period-${p.id}`}
              >{p.label}</Button>
            ))}
          </div>
        </div>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={DollarSign} label="Net payout" value={fmt(data.netPayout)} sub={`After ${(data.commission/Math.max(data.grossRevenue,1)*100).toFixed(0)}% commission`} />
              <StatCard icon={TrendingUp} label="Gross" value={fmt(data.grossRevenue)} sub={`Commission: ${fmt(data.commission)}`} />
              <StatCard icon={Users} label="Bookings" value={data.totalBookings} sub={`${data.totalSeats} seats sold`} />
              <StatCard icon={Calendar} label="Period" value={data.period} />
            </div>

            {data.dailySeries?.length > 0 && (
              <Card className="p-4">
                <h2 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-pink-600" /> Revenue Trend
                </h2>
                <div className="flex items-end gap-1 h-40" data-testid="operator-revenue-chart">
                  {data.dailySeries.map(d => (
                    <div key={d.date} className="flex-1 flex flex-col items-center group">
                      <div className="text-xs font-medium mb-1 opacity-0 group-hover:opacity-100 transition">
                        K{d.revenue.toFixed(0)}
                      </div>
                      <div
                        className="w-full bg-gradient-to-t from-pink-500 to-pink-300 rounded-t"
                        style={{ height: `${(d.revenue / maxSeries) * 100}%`, minHeight: d.revenue > 0 ? '4px' : '0' }}
                      />
                      <span className="text-[10px] text-gray-500 mt-1">{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OperatorRevenue;
